import { Plus, X } from 'lucide-react';
import { Select as UiSelect } from '../ui/Select';
import { formatCurrency } from '../../lib/format';
import type { InvoiceAllocation, ReconCandidatesDto } from '../../lib/bankStatementsApi';

// The „Factură” field of a bank line: one line can pay several invoices (a
// client paying three at once) and one invoice can be paid by several lines
// (instalments), on income AND on payments. Each row is an invoice plus the
// part of the line that goes to it; the first invoice is also the line's
// matchedInvoiceId.

type CandidateInvoice = ReconCandidatesDto['invoices'][number];

export function InvoiceAllocationsField({
  direction,
  lineAmount,
  lineCurrency,
  allocations,
  invoices,
  onChange,
}: {
  direction: 'credit' | 'debit';
  lineAmount: number;
  lineCurrency: string;
  allocations: InvoiceAllocation[];
  invoices: CandidateInvoice[];
  onChange: (next: InvoiceAllocation[]) => void;
}) {
  const pool = invoices.filter((i) => i.direction === direction);
  const byId = new Map(pool.map((i) => [i.id, i]));
  const allocated = round2(allocations.reduce((s, a) => s + (a.amount || 0), 0));
  const left = round2(Math.abs(lineAmount) - allocated);

  function label(i: CandidateInvoice): string {
    const rest =
      i.total < i.invoiceTotal - 0.005
        ? ` · rest ${formatCurrency(i.total, i.currency)}`
        : ` · ${formatCurrency(i.invoiceTotal, i.currency)}`;
    return `${i.number}${i.partnerName ? ` · ${i.partnerName}` : ''}${rest}`;
  }

  /** Default part for a newly chosen invoice: what it still needs, at most what is left. */
  function defaultAmount(invoiceId: string, available: number): number {
    const inv = byId.get(invoiceId);
    if (!inv || inv.currency.toUpperCase() !== lineCurrency.toUpperCase()) return available;
    // Nothing left to pay on it (or a credit note): offer what the line has.
    if (inv.total <= 0) return available;
    return round2(Math.min(available, inv.total));
  }

  function setRow(index: number, patch: Partial<InvoiceAllocation>) {
    const base =
      index < allocations.length ? allocations : [...allocations, { invoiceId: '', amount: 0 }];
    onChange(base.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  }

  function pick(index: number, invoiceId: string) {
    if (!invoiceId) {
      onChange(allocations.filter((_, i) => i !== index));
      return;
    }
    const inv = byId.get(invoiceId);
    const others = round2(allocations.reduce((s, a, i) => (i === index ? s : s + a.amount), 0));
    setRow(index, {
      invoiceId,
      invoiceNumber: inv?.number,
      invoiceCurrency: inv?.currency,
      invoiceTotal: inv?.invoiceTotal,
      amount: defaultAmount(invoiceId, round2(Math.abs(lineAmount) - others)),
      invoiceAmount: null,
    });
  }

  const rows = allocations.length > 0 ? allocations : [{ invoiceId: '', amount: 0 }];

  return (
    <div className="block min-w-0 sm:col-span-2">
      <span className="mb-1 block text-[11px] font-medium text-gray-500">
        {direction === 'credit' ? 'Facturi încasate' : 'Facturi plătite'}
      </span>
      <div className="space-y-1.5">
        {rows.map((a, index) => {
          const known = a.invoiceId ? byId.get(a.invoiceId) : undefined;
          // An invoice already paid (no longer a candidate) stays selectable here.
          const options = [
            ...(a.invoiceId && !known
              ? [{ value: a.invoiceId, label: a.invoiceNumber ?? a.invoiceId }]
              : []),
            ...pool
              .filter((i) => i.id === a.invoiceId || !allocations.some((x) => x.invoiceId === i.id))
              .map((i) => ({ value: i.id, label: label(i) })),
          ];
          const foreign =
            a.invoiceId &&
            (known?.currency ?? a.invoiceCurrency ?? lineCurrency).toUpperCase() !==
              lineCurrency.toUpperCase();
          return (
            <div key={`${a.invoiceId}-${index}`} className="flex flex-wrap items-center gap-2">
              <div className="min-w-[10rem] flex-1">
                <UiSelect
                  label="Factură"
                  hideLabel
                  size="sm"
                  clearable
                  searchable
                  value={a.invoiceId}
                  onChange={(v) => pick(index, v)}
                  placeholder="— fără factură —"
                  options={options}
                />
              </div>
              {a.invoiceId && (
                <>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    aria-label="Suma alocată din linie"
                    value={a.amount}
                    onChange={(e) => setRow(index, { amount: Number(e.target.value) || 0 })}
                    className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-xs"
                  />
                  <span className="text-xs text-gray-500">{lineCurrency}</span>
                  {foreign && (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      aria-label="Aceeași sumă în valuta facturii"
                      title="Aceeași sumă în valuta facturii (gol = linia acoperă restul facturii)"
                      placeholder={known?.currency ?? a.invoiceCurrency}
                      value={a.invoiceAmount ?? ''}
                      onChange={(e) =>
                        setRow(index, {
                          invoiceAmount: Number(e.target.value) > 0 ? Number(e.target.value) : null,
                        })
                      }
                      className="w-24 rounded border border-gray-300 px-2 py-1 text-right text-xs"
                    />
                  )}
                  <button
                    type="button"
                    aria-label="Scoate factura"
                    onClick={() => onChange(allocations.filter((_, i) => i !== index))}
                    className="text-gray-400 hover:text-red-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
      {allocations.length > 0 && (
        <div className="mt-1 flex items-center gap-3 text-[11px]">
          {left > 0.005 && (
            <button
              type="button"
              onClick={() => onChange([...allocations, { invoiceId: '', amount: 0 }])}
              className="inline-flex items-center gap-1 font-medium text-brand-700 hover:underline"
            >
              <Plus className="h-3 w-3" /> Încă o factură
            </button>
          )}
          <span className={left < -0.005 ? 'text-red-600' : 'text-gray-500'}>
            {left < -0.005
              ? `Alocat peste sumă cu ${formatCurrency(-left, lineCurrency)}`
              : left > 0.005
                ? `Nealocat: ${formatCurrency(left, lineCurrency)}`
                : 'Toată suma e alocată'}
          </span>
        </div>
      )}
    </div>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
